/**
 * Painel de criar/editar um item de catálogo da classificação. O código é
 * a identidade estável (o painel e o histórico agrupam por ele): só se
 * escolhe na criação. Item usado não se apaga — desativa e, se preciso,
 * aponta o substituto.
 */
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../../../components/AsyncButton";
import SlideOverPanel from "../../../../components/SlideOverPanel";
import { useAuth } from "../../../../context/AuthContext";
import {
  MOTIVOS_PECA,
  MOTIVOS_REQUISICAO,
  atualizarItemCatalogo,
  criarItemCatalogo,
} from "../../../../services/solicitacoes";
import {
  Checkbox, Field, GhostBtn, InlineError, Input, Label, RowEnd, Select,
} from "../styles";
import { CATALOGOS, normalizarCodigo } from "./regras";

const Corpo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Opcoes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  font-size: 12.5px;
  label { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const Ajuda = styled.div`
  font-size: 11.5px;
  color: var(--ink-3);
`;

// Motivos que o solicitante informa (peça + requisição), sem repetir "OUTRO".
const MOTIVOS = [...MOTIVOS_PECA, ...MOTIVOS_REQUISICAO].filter(
  (m, i, todos) => todos.findIndex((x) => x.value === m.value) === i,
);

function alternarNaLista(lista, valor) {
  return lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];
}

export default function CatalogoItemPanel({ tipo, item, itens, listas, onClose, onSaved }) {
  const { request } = useAuth();
  const novo = !item;
  const meta = CATALOGOS[tipo];
  const [f, setF] = useState(() => ({
    codigo: item?.codigo || "",
    nome: item?.nome || "",
    ordem: item?.ordem ?? ((itens?.length || 0) + 1) * 10,
    ativo: item?.ativo ?? true,
    substituido_por: item?.substituido_por || "",
    familia: item?.familia || "",
    nivel: item?.nivel || "PROCESSO",
    categoria_6m: item?.categoria_6m || "",
    imputacao_padrao: item?.imputacao_padrao || "",
    entra_no_custo_nq: item?.entra_no_custo_nq ?? true,
    motivos_origem: item?.motivos_origem || [],
    processos_sugeridos: (item?.processos_sugeridos || []).map(String),
  }));
  const [erro, setErro] = useState("");
  const set = (campo) => (valor) => { setErro(""); setF((a) => ({ ...a, [campo]: valor })); };

  const substitutos = useMemo(
    () => (itens || []).filter((i) => i.ativo && i.id !== item?.id),
    [itens, item],
  );

  const salvar = async () => {
    setErro("");
    const nome = f.nome.trim();
    const codigo = normalizarCodigo(f.codigo || nome);
    if (!nome) { setErro("Informe o nome."); throw new Error("nome"); }
    if (novo && !codigo) { setErro("Informe o código."); throw new Error("codigo"); }
    if (tipo === "ncs" && !f.familia) { setErro("Escolha a família da não conformidade."); throw new Error("familia"); }
    if (tipo === "causas" && !f.categoria_6m) { setErro("Escolha a categoria 6M."); throw new Error("6m"); }

    const dados = { nome, ordem: Number(f.ordem) || 0, ativo: f.ativo };
    if (novo) dados.codigo = codigo;
    if (!novo) dados.substituido_por = !f.ativo && f.substituido_por ? f.substituido_por : null;
    if (tipo === "demandas") {
      dados.imputacao_padrao = f.imputacao_padrao;
      dados.entra_no_custo_nq = f.entra_no_custo_nq;
    }
    if (tipo === "ncs") {
      dados.familia = f.familia;
      dados.motivos_origem = f.motivos_origem;
      dados.processos_sugeridos = f.processos_sugeridos;
    }
    if (tipo === "processos") {
      dados.nivel = f.nivel;
      dados.imputacao_padrao = f.imputacao_padrao;
    }
    if (tipo === "causas") dados.categoria_6m = f.categoria_6m;

    try {
      if (novo) await criarItemCatalogo(request, tipo, dados);
      else await atualizarItemCatalogo(request, tipo, item.id, dados);
      onSaved?.();
    } catch (e) {
      setErro(e?.message || "Não foi possível salvar.");
      throw e;
    }
  };

  const footer = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {erro && <InlineError style={{ fontSize: 12.5 }}>{erro}</InlineError>}
      <RowEnd>
        <GhostBtn type="button" onClick={onClose}>Voltar</GhostBtn>
        <AsyncButton variant="primary" silentError successMessage="Catálogo atualizado." onClick={salvar}>
          {novo ? "Criar" : "Salvar"}
        </AsyncButton>
      </RowEnd>
    </div>
  );

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={novo ? `Nova ${meta.singular}` : item.nome}
      subtitle={meta.hint}
      footer={footer}
    >
      <Corpo>
        <Field>
          <Label>Nome *</Label>
          <Input value={f.nome} onChange={(e) => set("nome")(e.target.value)} autoFocus />
        </Field>
        <Field>
          <Label>Código {novo ? "*" : "(não muda depois de criado)"}</Label>
          <Input
            value={novo ? f.codigo : item.codigo}
            disabled={!novo}
            onChange={(e) => set("codigo")(e.target.value)}
            onBlur={() => set("codigo")(normalizarCodigo(f.codigo || f.nome))}
            placeholder={normalizarCodigo(f.nome) || "EX_CODIGO"}
            className="mono"
          />
          {novo && <Ajuda>É a identidade do item nos relatórios: escolha com calma, depois não muda.</Ajuda>}
        </Field>
        <Field style={{ maxWidth: 140 }}>
          <Label>Ordem na lista</Label>
          <Input type="number" value={f.ordem} onChange={(e) => set("ordem")(e.target.value)} />
        </Field>

        {tipo === "ncs" && (
          <>
            <Field>
              <Label>Família (agrupa o Pareto) *</Label>
              <Select value={f.familia} onChange={(e) => set("familia")(e.target.value)} style={{ height: 38 }}>
                <option value="">— escolha —</option>
                {(listas?.familias || []).map((o) => <option key={o.codigo} value={o.codigo}>{o.nome}</option>)}
              </Select>
            </Field>
            <Field>
              <Label>Motivos do solicitante que sugerem esta NC</Label>
              <Opcoes>
                {MOTIVOS.map((m) => (
                  <label key={m.value}>
                    <Checkbox checked={f.motivos_origem.includes(m.value)}
                              onChange={() => set("motivos_origem")(alternarNaLista(f.motivos_origem, m.value))} />
                    <span>{m.label}</span>
                  </label>
                ))}
              </Opcoes>
              <Ajuda>No modal de autorizar, esta NC aparece primeiro quando o item traz um desses motivos.</Ajuda>
            </Field>
            <Field>
              <Label>Processos de origem sugeridos</Label>
              <Opcoes>
                {(listas?.processos || []).map((p) => (
                  <label key={p.id}>
                    <Checkbox checked={f.processos_sugeridos.includes(String(p.id))}
                              onChange={() => set("processos_sugeridos")(alternarNaLista(f.processos_sugeridos, String(p.id)))} />
                    <span>{p.nome}</span>
                  </label>
                ))}
              </Opcoes>
            </Field>
          </>
        )}

        {tipo === "processos" && (
          <Field>
            <Label>Nível</Label>
            <Select value={f.nivel} onChange={(e) => set("nivel")(e.target.value)} style={{ height: 38 }}>
              {(listas?.niveis || []).map((o) => <option key={o.codigo} value={o.codigo}>{o.nome}</option>)}
            </Select>
          </Field>
        )}

        {tipo === "causas" && (
          <Field>
            <Label>Categoria 6M (a mesma da Produção) *</Label>
            <Select value={f.categoria_6m} onChange={(e) => set("categoria_6m")(e.target.value)} style={{ height: 38 }}>
              <option value="">— escolha —</option>
              {(listas?.categorias_6m || []).map((o) => <option key={o.codigo} value={o.codigo}>{o.nome}</option>)}
            </Select>
          </Field>
        )}

        {(tipo === "demandas" || tipo === "processos") && (
          <Field>
            <Label>Imputação padrão (quem arca, quando a regra não decide)</Label>
            <Select value={f.imputacao_padrao} onChange={(e) => set("imputacao_padrao")(e.target.value)} style={{ height: 38 }}>
              <option value="">— sem padrão —</option>
              {(listas?.imputacoes || []).map((o) => <option key={o.codigo} value={o.codigo}>{o.nome}</option>)}
            </Select>
          </Field>
        )}

        {tipo === "demandas" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <Checkbox checked={f.entra_no_custo_nq} onChange={(e) => set("entra_no_custo_nq")(e.target.checked)} />
            Entra no custo da não-qualidade
          </label>
        )}

        {!novo && (
          <>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <Checkbox checked={f.ativo} disabled={item.sistema}
                        onChange={(e) => set("ativo")(e.target.checked)} />
              Ativo (aparece para escolher no modal de autorizar)
            </label>
            {item.sistema && <Ajuda>Item de sistema: recebe o que ficou sem classificar e não pode ser desativado.</Ajuda>}
            {!f.ativo && (
              <Field>
                <Label>Substituído por (o painel soma no vigente)</Label>
                <Select value={f.substituido_por || ""} onChange={(e) => set("substituido_por")(e.target.value)} style={{ height: 38 }}>
                  <option value="">— nenhum —</option>
                  {substitutos.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </Select>
              </Field>
            )}
          </>
        )}
      </Corpo>
    </SlideOverPanel>
  );
}
